import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { GoApplicationPreparationBaseComponent } from '../shared/components/go-application-preparation-base/go-application-preparation-base.component';
import { GoApplicationRequestFormModel } from '../shared/models/go-application-request-form-model';
import { GoApplicationPurposeSelectionComponent } from '../shared/components/go-application-purpose-selection/go-application-purpose-selection.component';
import { FormGroup } from '@angular/forms';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { GoParametersSummaryComponent } from '../shared/components/go-parameters-summary/go-parameters-summary.component';
import { interval, Subject, takeUntil, tap } from 'rxjs';
import { ApplicationFilingStatusEnum } from '../../shared/constants/application-filing-status';
import { LoadingDotsComponent } from '../../../../shared/components/loading-dots/loading-dots.component';
import { MatButtonModule } from '@angular/material/button';
import { ApplicationState } from '../../../../core/models/application-state/application-state';
import { GoApplicationPreparationService } from '../shared/services/go-application-preparation/go-application-preparation.service';
import { Router, RouterModule } from '@angular/router';
import { ApplicationPageFacadeService } from '../../direct-entry/shared/services/application-page-facade/application-page-facade.service';
import { GoApplicationService } from '../shared/services/go-application/go-application.service';
import { GoManageApplicationService } from '../shared/services/go-manage-application/go-manage-application.service';
import { RouteConstants } from '../../../../core/constants/routes';
import { ApplicationService } from '../../shared/services/application/application.service';
import { GoStorageService } from '../go-parameters/services/go-storage/go-storage.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { SearchFailedModalConstants } from './constants/search-failed-modal-constants';
import { hasOpenDialogs } from '../../../../core/functions/dialog-helper';

@Component({
    selector: 'app-go-search-processing',
    imports: [
        GoApplicationPurposeSelectionComponent,
        GoParametersSummaryComponent,
        LoadingDotsComponent,
        MatButtonModule,
        RouterModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './go-search-processing.component.html',
})
export class GoSearchProcessingComponent
    extends GoApplicationPreparationBaseComponent<GoApplicationRequestFormModel>
    implements OnInit
{
    form: FormGroup<FormGroupType<GoApplicationRequestFormModel>> =
        this.goApplicationRequestForm;

    hasChanges = false;

    applicationState: ApplicationState | null = null;

    private stopApplicationStatePolling$ = new Subject<void>();

    private APPLICATION_STATE_INTERVAL = 5000;

    constructor(
        protected override readonly goApplicationPreparationService: GoApplicationPreparationService,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        protected override readonly goApplicationService: GoApplicationService,
        protected override readonly router: Router,
        protected override readonly goStorageService: GoStorageService,
        private readonly goManageApplicationService: GoManageApplicationService,
        private readonly applicationService: ApplicationService,
        private readonly dialog: MatDialog,
    ) {
        super(
            goApplicationPreparationService,
            applicationPageFacadeService,
            goApplicationService,
            router,
            goStorageService,
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.applicationState =
            this.applicationPageFacadeService.applicationStateService.getCurrentApplicationState();

        interval(this.APPLICATION_STATE_INTERVAL)
            .pipe(
                // Unsubscribe from the interval when the component is destroyed or when intentionally stopping the polling
                takeUntil(this.destroy$),
                takeUntil(this.stopApplicationStatePolling$),
                tap(() => {
                    this.applicationService
                        .getApplicationState(
                            this.applicationState!.applicationId!.toString(),
                        )
                        .pipe(
                            takeUntil(this.destroy$),
                            takeUntil(this.stopApplicationStatePolling$),
                            tap((state: ApplicationState | null) => {
                                const filingStatus = state?.filingStatus;

                                if (
                                    filingStatus ===
                                    ApplicationFilingStatusEnum.coordinationResultsReady
                                ) {
                                    this.router.navigate([
                                        RouteConstants.Go.getFullRouteWithApplicationId(
                                            'frequencyResults',
                                            state!.applicationId.toString(),
                                        ),
                                    ]);
                                } else if (
                                    filingStatus ===
                                    ApplicationFilingStatusEnum.coordinationFailed
                                ) {
                                    // Stop the interval from making further calls to the API
                                    this.stopApplicationStatePolling$.next();

                                    if (!hasOpenDialogs(this.dialog)) {
                                        this.dialog.open(ModalComponent, {
                                            disableClose: true,
                                            data: {
                                                title: SearchFailedModalConstants.searchFailedModalTitle,
                                                content:
                                                    SearchFailedModalConstants.searchFailedModalContent,
                                                primaryAction: {
                                                    title: SearchFailedModalConstants.searchFailedModalPrimaryAction,
                                                    action: () => {
                                                        this.router.navigate([
                                                            RouteConstants.Go.getFullRouteWithApplicationId(
                                                                'parametersWithApplicationId',
                                                                state!.applicationId.toString(),
                                                            ),
                                                        ]);
                                                    },
                                                },
                                                isShowCancel: false,
                                            },
                                        });
                                    }
                                }
                            }),
                        )
                        .subscribe();
                }),
            )
            .subscribe();
    }

    terminateSearch(): void {
        const applicationId = this.applicationState?.applicationId;

        if (!applicationId) {
            throw new Error(
                'Application ID is required to terminate the search.',
            );
        }

        this.goManageApplicationService.terminateSearch(
            applicationId.toString(),
        );
    }

    stopAndSaveSearch(): void {
        const applicationId = this.applicationState?.applicationId;

        if (!applicationId) {
            throw new Error(
                'Application ID is required to stop and save the search.',
            );
        }

        this.goManageApplicationService.stopAndSaveSearch(
            applicationId.toString(),
        );
    }

    protected override shouldSubmit(): boolean {
        return false;
    }

    protected override beforeStepContinue(): Promise<boolean> {
        // No additional actions needed before moving to the next page
        return Promise.resolve(true);
    }

    protected override patchFormWithGoApplicationRequestFormValue(): void {
        // No need to patch the form as this page does not have any controls of it's own.
    }

    protected override scrollToFirstInvalidElement(): void {
        // No need to implement, does not have any input elements
    }
}
